import { Radio, Text } from '@mantine/core';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { DocumentTable } from '@dealstack/frontend/shared/components/Documents/DocumentTable/DocumentTable';
import { DuplicateFile } from '@dealstack/frontend/shared/components/Documents/types';
import { useMantineTableWithPaginationFilteringSorting } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { getFileListColumns } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/ValidateFileStep/getFileListColumns';
import {
	FileItemContainer,
	FileItemInnerContainer,
	FileSelectorContainer,
	FileTableContainer,
	SidePanelContainer,
} from '@dealstack/frontend/shared/components/Documents/UploadDocuments/ValidateFileStep/styled';
import {
	H5,
	StepContainer,
} from '@dealstack/frontend/shared/components/Documents/UploadDocuments/styled';
import { UseMantineTableDataOperationsPartialProps } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperationsFromUrlParamState';
import { useEffect, useState } from 'react';
import {
	DOCUMENT_LIBRARY_ELEMENT_ID,
	makeComplexDataTestId,
} from '@dealstack/shared/tests/testElementIds';

interface Props {
	localDuplicateFiles: {
		filesWithDuplicates: DuplicateFile[];
		totalDuplicates: number;
	};
	setLocalDuplicateFiles: (localDuplicateFiles: {
		filesWithDuplicates: DuplicateFile[];
		totalDuplicates: number;
	}) => void;
	setCanButtonProceed: (canButtonProceed: boolean) => void;
}

export const ValidateFileStep = ({
	setCanButtonProceed,
	localDuplicateFiles,
	setLocalDuplicateFiles,
}: Props) => {
	const [selectedFile, setSelectedFile] = useState<DuplicateFile | null>(null);

	// when loading up set the first file to be selected
	useEffect(() => {
		if (selectedFile === null && localDuplicateFiles.filesWithDuplicates.length > 0) {
			setSelectedFile(localDuplicateFiles.filesWithDuplicates[0]);
		}
	}, [localDuplicateFiles.filesWithDuplicates, selectedFile]);

	const toggleSelected = (index: number) => {
		const filesWithDuplicates = localDuplicateFiles.filesWithDuplicates.map((duplicateFile) => {
			const foundSubRow = duplicateFile?.subRows?.find((subRow) => subRow.index === index);
			if (foundSubRow) {
				return {
					...duplicateFile,
					status: true,
					subRows: duplicateFile?.subRows?.map((subRow) => ({
						...subRow,
						isSelected: index === subRow.index ? true : false,
					})),
				};
			}

			return duplicateFile;
		});

		// check that all the files have been reviewed that need to be.
		const isAllReviewed = filesWithDuplicates?.every((file) =>
			file.subRows?.some((file) => file.isSelected === true)
		);

		setCanButtonProceed(isAllReviewed);

		setLocalDuplicateFiles({
			...localDuplicateFiles,
			filesWithDuplicates,
		});
	};

	const tCapManage = useTranslationCapManage();
	const columns = getFileListColumns({ tCapManage, toggleSelected });
	const initialTableOperations: UseMantineTableDataOperationsPartialProps = {
		initialPagination: {
			pageIndex: 0,
			pageSize: 10,
		},
	};
	const { mantineTableDataOperations } = useMantineTableWithPaginationFilteringSorting(
		columns,
		initialTableOperations
	);

	return (
		<StepContainer data-testid={DOCUMENT_LIBRARY_ELEMENT_ID.UPLOAD_VALIDATE_FILES_STEP}>
			<FileTableContainer>
				<DocumentTable<DuplicateFile>
					enableRenderBottomToolbarCustomActions={false}
					enableRowSelection={false}
					enablePagination={false}
					enableRowActions={false}
					dataOperations={mantineTableDataOperations}
					hasNoTopToolbar={true}
					onRowClick={(row) => {
						setSelectedFile(row.original);
					}}
					enableTopToolbar={false}
					columns={columns}
					isLoading={false}
					data={localDuplicateFiles.filesWithDuplicates}
				/>
			</FileTableContainer>
			<SidePanelContainer>
				<H5>{tCapManage('document.duplicate_files.side_bar.header')}</H5>
				<Text>
					{tCapManage('document.duplicate_files.side_bar.sub_header', {
						nbr_files: selectedFile?.numberOfDuplicates,
					})}
				</Text>
				<FileSelectorContainer>
					{localDuplicateFiles.filesWithDuplicates
						?.find((file) => file.md5 === selectedFile?.md5)
						?.subRows?.map((file) => (
							<FileItemContainer
								$isSelected={!!file.isSelected}
								key={file.file.name}
								data-testid={makeComplexDataTestId(
									DOCUMENT_LIBRARY_ELEMENT_ID.UPLOAD_DUPLICATE_CANDIDATE,
									file.file.name
								)}
							>
								<FileItemInnerContainer>
									<Radio
										data-testid={makeComplexDataTestId(
											DOCUMENT_LIBRARY_ELEMENT_ID.UPLOAD_DUPLICATE_CANDIDATE_RADIO,
											file.file.name
										)}
										checked={file.isSelected}
										onChange={() => toggleSelected(file.index)}
									/>
									<Text>{file.file.name}</Text>
								</FileItemInnerContainer>
							</FileItemContainer>
						))}
				</FileSelectorContainer>
			</SidePanelContainer>
		</StepContainer>
	);
};
